/**
 * Tela dos documentos pessoais da família.
 *
 * Papel: junta a tabela dos documentos, o formulário em caixa de diálogo e a
 * confirmação de exclusão.
 *
 * Como usar: quem tem nível `ver` não recebe `onNovo`, `onEditar` nem
 * `onRemover`, e por isso não vê botão de ação nenhum. Mostrá-los desabilitados
 * ofereceria uma ação que a API recusaria com 403 depois do clique.
 *
 * O subtítulo da seção enumera, depois do travessão, os apelidos das pessoas
 * que têm documento, na ordem da listagem, por `formatarListaEmCaixaAlta`: uma
 * pessoa nova com documento entra nele sem mudança de código, e sem documento
 * nenhum o subtítulo termina nos tipos, sem travessão pendurado.
 *
 * O aviso de vencimento não fica aqui: ele mora na tela inicial, onde é visto
 * sem que se precise abrir o módulo e onde reúne também os planos de saúde.
 * `apurarVencimentosDeDocumentos` sai pelo `index.ts` deste módulo por isso.
 */
import { type Documento, ROTULOS_MODULO, acessoAlcanca } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { formatarListaEmCaixaAlta } from '@/shared/lib/formato';
import { hojeEmIso } from '@/shared/lib/vencimento';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecaoCadastro } from '@/shared/ui/secao-cadastro';
import { FormularioDocumento } from '../components/formulario-documento';
import { TabelaDocumentos } from '../components/tabela-documentos';
import { useDocumentos, useExcluirDocumento } from '../hooks/usar-documentos';

/** Os tipos de documento que a seção cobre, na abertura do subtítulo. */
const TIPOS_DO_SUBTITULO = 'PASSAPORTE, RG, CNH E VISTO';

/** Em que estado a caixa do formulário está: fechada, criando ou editando. */
type PainelAberto =
  { modo: 'fechado' } | { modo: 'novo' } | { modo: 'edicao'; documento: Documento };

/**
 * Página de Documentos.
 *
 * @returns A tela com a tabela, a caixa do formulário e a confirmação.
 */
export function PaginaDocumentos() {
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<Documento | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'documentos', 'editar');

  const consulta = useDocumentos();
  const excluir = useExcluirDocumento();
  const hoje = hojeEmIso();
  const apelidos = formatarListaEmCaixaAlta(
    (consulta.data ?? []).map((documento) => documento.pessoa.apelido),
  );

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(documento: Documento) {
    setErroDaRemocao(null);
    setARemover(documento);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o documento confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      await excluir.mutateAsync(aRemover.id);
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <div>
      <CabecalhoDoModulo titulo={ROTULOS_MODULO.documentos} />

      <SecaoCadastro
        titulo="Documentos pessoais da família"
        descricao={
          apelidos === '' ? TIPOS_DO_SUBTITULO : `${TIPOS_DO_SUBTITULO} — ${apelidos}`
        }
        rotuloNovo="+ Novo documento"
        onNovo={podeEditar ? () => setPainel({ modo: 'novo' }) : undefined}
        carregando={consulta.isPending}
        erro={consulta.error}
        onTentarDeNovo={() => void consulta.refetch()}
        vazia={(consulta.data ?? []).length === 0}
        mensagemVazia="Nenhum documento cadastrado ainda."
      >
        <TabelaDocumentos
          documentos={consulta.data ?? []}
          hoje={hoje}
          onEditar={
            podeEditar ? (documento) => setPainel({ modo: 'edicao', documento }) : undefined
          }
          onRemover={podeEditar ? handlePedirRemocao : undefined}
        />
      </SecaoCadastro>

      <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {painel.modo !== 'fechado' && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {painel.modo === 'novo' ? 'Novo documento' : 'Editar documento'}
                </DialogTitle>
              </DialogHeader>
              <FormularioDocumento
                key={painel.modo === 'novo' ? 'novo' : painel.documento.id}
                documento={painel.modo === 'novo' ? null : painel.documento}
                onSalvo={() => setPainel({ modo: 'fechado' })}
                onCancelar={() => setPainel({ modo: 'fechado' })}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remover{' '}
              {aRemover === null ? '' : `${aRemover.tipo} de ${aRemover.pessoa.nomeCompleto}`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A linha sai do cadastro, e com ela o registro de que aquele documento existe. Não
              há como desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluir.isPending}
              onClick={(evento) => {
                // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                // antes da resposta da API desmontaria o estado de envio e
                // esconderia a falha. O fechamento passa a ser nosso.
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
            >
              {excluir.isPending ? 'Removendo…' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
